import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import ReviewMasterModal from './ReviewMasterModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

// import { Review } from "../../../../types/Review";
const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));


interface Review {
  review_id: number;
  review_customer_id: string;
  review_product_id: string;
  review_invoice_request_id: string;
  review_type: string;
  review_rating: string;
  review_comment: string;
  review_status: number;
}



const ReviewMaster: React.FC = () => {
  const [reviews, setReview] = useState<Review[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Review | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // const fetchreviews = async () => {
  //   try {
  //     const data = await apiRequest<Review[]>({
  //       method: 'get',
  //       url: 'reviews',
  //     });
  //     setReview(data);
  //   } catch (error) {
  //     showError('Failed to fetch Review');
  //     console.error(error);
  //   }
  // };

  const fetchreviews = async () => {
    try {
      const data = await apiRequest<Review[]>({
        method: 'get',
        url: 'reviews',
      });

      // API सीधे array return कर रही है
      setReview(Array.isArray(data) ? data : []);
    } catch (error) {
      showError('Failed to fetch Review');
      console.error(error);
    }
  };


  useEffect(() => {
    fetchreviews();
  }, []);

  const handleEditClick = (record: Review) => {
    setIsEditMode(true);
    setInitialValues({
      review_id: record.review_id,
      review_customer_id: record.review_customer_id,
      review_product_id: record.review_product_id,
      review_invoice_request_id: record.review_invoice_request_id,
      review_type: record.review_type,
      review_rating: record.review_rating,
      review_comment: record.review_comment,
      review_status: record.review_status,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchreviews();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (review_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-review/${review_id}`,
      });
      showSuccess('Review deleted');
      fetchreviews();
    } catch {
      showError('Failed to delete Review');
    }
  };

  const handleStatusToggle = async (qnId: number) => {
    setReview((prev) =>
      prev.map((item) =>
        item.review_id === qnId
          ? { ...item, review_status: item.review_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-review/${qnId}`,
      });
      showSuccess('Review status updated');
    } catch (error) {
      showError('Failed to update status');
      setReview((prev) =>
        prev.map((item) =>
          item.review_id === qnId
            ? { ...item, review_status: item.review_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Review>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<Review> = [
    {
      title: 'Name',
      dataIndex: 'review_customer_id',
      key: 'review_customer_id',
      filters: Array.from(new Set(reviews.map((s) => s.review_customer_id))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.review_customer_id || null,
      onFilter: (value, record) => record.review_customer_id.includes(value as string),
      sorter: (a, b) => a.review_customer_id.localeCompare(b.review_customer_id),
      sortOrder: sortedInfo.columnKey === 'review_customer_id' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.review_customer_id}</div>,
    },
    {
      title: 'Rating',
      dataIndex: 'review_rating',
      key: 'review_rating',
      filters: Array.from(new Set(reviews.map((s) => s.review_rating))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.review_rating || null,
      onFilter: (value, record) => record.review_rating.includes(value as string),
      sorter: (a, b) => a.review_rating.localeCompare(b.review_rating),
      sortOrder: sortedInfo.columnKey === 'review_rating' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.review_rating}</div>,
    },
    {
      title: 'Comment',
      dataIndex: 'review_comment',
      key: 'review_comment',
      filters: Array.from(new Set(reviews.map((s) => s.review_comment))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.review_comment || null,
      onFilter: (value, record) => record.review_comment.includes(value as string),
      sorter: (a, b) => a.review_comment.localeCompare(b.review_comment),
      sortOrder: sortedInfo.columnKey === 'review_rating' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.review_comment}</div>,
    }
  ];



  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Review) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.review_status}
                onChange={() => handleStatusToggle(record.review_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this Review?"
              onConfirm={() => handleDelete(record.review_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Reviews</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Review
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Review>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="review_id"
          dataSource={reviews.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 15 }}
          locale={{ emptyText: 'No Review found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, reviews.length)} of{' '}
                {reviews.length}
              </span>
              <Pagination
                {...pagination}
                total={reviews.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <ReviewMasterModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default ReviewMaster;